import React, { useEffect, useState } from "react";
import { ArrowRight, Search } from "lucide-react";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import { GenerateShortToken } from "../../apis/Auth";
import { SearchRecords } from "../../apis/User";
import Paginate from "../../components/shared/paginate";

const USERS_PER_PAGE = "10";
const USER_SEARCH_LIMIT = "100";
const PHONE_SEARCH_MIN_DIGITS = 3;

const getPhoneDigits = (value) => String(value || "").replace(/\D/g, "");

const getUserRoleLabel = (user) => {
  const role = user.role?.name;

  if (role) return role;
  return "Unknown";
};

const isPhoneSearch = (keyword) => {
  const trimmedKeyword = keyword.trim();
  return (
    /^[\d\s()+.-]+$/.test(trimmedKeyword) &&
    getPhoneDigits(trimmedKeyword).length >= PHONE_SEARCH_MIN_DIGITS
  );
};

const CreateMultiUnitListing = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchUsers = async () => {
      setIsLoading(true);
      setUsers([]);
      try {
        if (isPhoneSearch(keyword)) {
          const firstPageResponse = await SearchRecords(
            "",
            "1",
            USER_SEARCH_LIMIT,
            "all",
          );

          if (!firstPageResponse.success) {
            throw new Error("Failed to fetch users.");
          }

          const firstPageData = firstPageResponse.data;
          const totalPages = Math.max(
            1,
            Number(firstPageData?.totalPages) || 1,
          );
          const otherPageResponses = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              SearchRecords(
                "",
                String(index + 2),
                USER_SEARCH_LIMIT,
                "all",
              ),
            ),
          );

          if (otherPageResponses.some((page) => !page.success)) {
            throw new Error("Failed to fetch users.");
          }

          const allUsers = [
            ...(firstPageData?.results || []),
            ...otherPageResponses.flatMap((page) => page.data?.results || []),
          ];
          const searchDigits = getPhoneDigits(keyword);
          const matchingUsers = allUsers.filter((user) => {
            const phoneDigits = getPhoneDigits(user.mobileNumber);
            return (
              phoneDigits.length >= PHONE_SEARCH_MIN_DIGITS &&
              (phoneDigits.includes(searchDigits) ||
                searchDigits.includes(phoneDigits))
            );
          });
          const pageSize = Number(USERS_PER_PAGE);
          const matchingTotalPages = Math.max(
            1,
            Math.ceil(matchingUsers.length / pageSize),
          );
          const requestedPage = Math.min(
            Number(currentPage),
            matchingTotalPages,
          );
          const pageStart = (requestedPage - 1) * pageSize;

          if (isCurrentRequest) {
            setUsers(matchingUsers.slice(pageStart, pageStart + pageSize));
            setTotalPages(String(matchingTotalPages));
            if (requestedPage !== Number(currentPage)) {
              setCurrentPage(String(requestedPage));
            }
          }
          return;
        }

        const response = await SearchRecords(
          keyword.trim(),
          currentPage,
          USERS_PER_PAGE,
          "all",
        );

        if (!response.success) {
          throw new Error("Failed to fetch users.");
        }

        if (isCurrentRequest) {
          setUsers(response.data?.results || []);
          setTotalPages(String(response.data?.totalPages || 1));
        }
      } catch {
        if (isCurrentRequest) {
          setTotalPages("1");
          toast.error("Failed to fetch users. Please try again later.");
        }
      } finally {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      }
    };

    fetchUsers();
    return () => {
      isCurrentRequest = false;
    };
  }, [currentPage, keyword]);

  const handleSearch = (event) => {
    setKeyword(event.target.value);
    setCurrentPage("1");
  };

  const startListingCreation = async () => {
    if (!selectedUser || isStarting) return;

    setIsStarting(true);
    try {
      const response = await GenerateShortToken();
      const token = response.data?.token;
      if (!token) {
        throw new Error("A token was not returned.");
      }

      const clientUrl = new URL(
        "/listing/create/multi-unit",
        process.env.REACT_APP_CLIENTSIDE_URL,
      );
      clientUrl.searchParams.set("token", token);
      clientUrl.searchParams.set("hostId", selectedUser._id);
      window.open(clientUrl.toString(), "_blank");
    } catch {
      toast.error("Failed to start listing creation. Please try again.");
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="page-content">
      <Container
        className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]"
        fluid
      >
        <div className="flex flex-col gap-6 p-6">
          <div>
            <h1 className="text-2xl dark:text-[#c7d8eb]">
              Create new listing for any user
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Choose the user who will own this new listing. Guests will become
              hosts when they start creating it.
            </p>
          </div>

          <div className="relative w-full max-w-[480px] rounded-md border border-gray-500 dark:border-white">
            <Search
              size={16}
              className="absolute bottom-1/2 left-2 translate-y-1/2"
            />
            <input
              className="w-full rounded-md py-2 pl-8 pr-6 text-base dark:bg-darkBg2"
              type="search"
              placeholder="Search users by name, email, or mobile number"
              value={keyword}
              onChange={handleSearch}
              aria-label="Search users"
            />
          </div>

          <div className="flex flex-col gap-3">
            {isLoading ? (
              <div className="py-8 text-center">Loading users...</div>
            ) : users.length ? (
              users.map((user) => {
                const isSelected = selectedUser?._id === user._id;
                const userName =
                  [user.firstName, user.lastName].filter(Boolean).join(" ") ||
                  "Unnamed user";
                const userRole = getUserRoleLabel(user);

                return (
                  <button
                    key={user._id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelectedUser(user)}
                    className={`flex w-full flex-wrap items-center gap-4 rounded-md border p-4 text-left ${isSelected
                      ? "border-secondaryColor bg-secondaryColor/10"
                      : "border-gray-400 dark:border-white"
                      }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${isSelected
                        ? "border-secondaryColor"
                        : "border-gray-500 dark:border-white"
                        }`}
                      aria-hidden="true"
                    >
                      {isSelected && (
                        <span className="h-2.5 w-2.5 rounded-full bg-secondaryColor" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold">{userName}</span>
                      <span className="block break-all text-sm text-gray-600 dark:text-gray-300">
                        {user.email || "No email"}
                      </span>
                      <span className="block text-sm text-gray-600 dark:text-gray-300">
                        {user.mobileNumber || "No phone number"}
                      </span>
                    </span>
                    <span className="text-sm capitalize text-gray-600 dark:text-gray-300">
                      {userRole}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="border-y border-gray-400 py-8 text-center dark:border-white">
                No users found.
              </div>
            )}
          </div>

          {!isLoading && (
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          )}

          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {selectedUser
                ? `Selected user: ${[selectedUser.firstName, selectedUser.lastName]
                  .filter(Boolean)
                  .join(" ") || selectedUser.email
                }`
                : "Select a user to continue."}
            </p>
            <button
              type="button"
              onClick={startListingCreation}
              disabled={!selectedUser || isStarting}
              className="flex items-center gap-2 rounded-md bg-secondaryColor px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isStarting ? "Starting..." : "Start creating listing"}
              {!isStarting && <ArrowRight size={16} />}
            </button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default CreateMultiUnitListing;
